import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePlus, Upload, Search, X } from "lucide-react";

// Reusable media picker: pick an existing library image OR upload a new one.
// Value = { url, alt }. onChange receives the same shape (url is a /api/files/... path).
export function MediaPicker({ value, onChange, label = "Image", testid = "media-picker" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("library");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ title: "", alt: "", caption: "" });
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/cms/media-library`, { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { if (open && tab === "library") load(); /* eslint-disable-next-line */ }, [open, tab, q]);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose an image file first");
    if (!form.alt.trim()) return toast.error("Alt text is required for public images");
    const fd = new FormData();
    fd.append("file", f); fd.append("title", form.title || f.name); fd.append("alt", form.alt); fd.append("caption", form.caption);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/cms/media-library`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to media library");
      onChange({ url: data.url, alt: data.alt });
      setForm({ title: "", alt: "", caption: "" }); if (fileRef.current) fileRef.current.value = "";
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="mt-1 flex items-center gap-3">
        <div className="flex h-16 w-24 items-center justify-center overflow-hidden rounded-lg border border-border bg-cream/40">
          {value?.url ? <img src={fileUrl(value.url)} alt={value.alt || ""} className="h-full w-full object-cover" data-testid={`${testid}-preview`} /> : <ImagePlus className="h-5 w-5 text-muted-foreground" />}
        </div>
        <div className="flex flex-col gap-1">
          <Button type="button" size="sm" variant="outline" onClick={() => { setOpen(true); setTab("library"); }} data-testid={`${testid}-open`} className="rounded-full text-xs">Choose / Upload</Button>
          {value?.url && <button type="button" onClick={() => onChange({ url: "", alt: "" })} data-testid={`${testid}-clear`} className="flex items-center gap-1 text-xs text-maroon hover:underline"><X className="h-3 w-3" />Remove</button>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle className="font-display text-xl">Media Library</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Button size="sm" variant={tab === "library" ? "default" : "outline"} onClick={() => setTab("library")} data-testid={`${testid}-tab-library`} className={`rounded-full ${tab === "library" ? "bg-charcoal text-white" : ""}`}>Library</Button>
            <Button size="sm" variant={tab === "upload" ? "default" : "outline"} onClick={() => setTab("upload")} data-testid={`${testid}-tab-upload`} className={`rounded-full ${tab === "upload" ? "bg-charcoal text-white" : ""}`}><Upload className="mr-1 h-3.5 w-3.5" />Upload</Button>
          </div>

          {tab === "library" && (
            <div>
              <div className="relative my-3"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search media…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid={`${testid}-search`} /></div>
              {items.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No media yet. Upload an image to get started.</p> : (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {items.map((m) => (
                    <button key={m.id} type="button" onClick={() => { onChange({ url: m.url, alt: m.alt }); setOpen(false); }} data-testid={`${testid}-item-${m.id}`}
                      className="group overflow-hidden rounded-lg border border-border hover:border-saffron">
                      <img src={fileUrl(m.url)} alt={m.alt || m.title} className="h-24 w-full object-cover" />
                      <div className="truncate px-1 py-0.5 text-[10px] text-muted-foreground">{m.title || m.original_filename}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "upload" && (
            <div className="mt-3 space-y-3">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" data-testid={`${testid}-file`} className="block w-full text-sm" />
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="text-xs font-semibold">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={`${testid}-title`} /></div>
                <div><Label className="text-xs font-semibold">Alt text *</Label><Input value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} data-testid={`${testid}-alt`} placeholder="Describe the image" /></div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold">Caption (optional)</Label><Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid={`${testid}-caption`} /></div>
              </div>
              <Button onClick={upload} disabled={busy} data-testid={`${testid}-submit`} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">{busy ? "Uploading…" : "Upload & Select"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
